import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Users, Plus, X, AlertTriangle, Search } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function MembersTab({ pw }) {
  const authHeaders = useMemo(() => ({ "X-Admin-Password": pw }), [pw]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);        // member being edited
  const [adding, setAdding] = useState(false);
  const [includeCancelled, setIncludeCancelled] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/members?include_cancelled=${includeCancelled}`, { headers: authHeaders });
      setRows(r.data.members || []);
    } catch (e) {
      toast.error(`Members load failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setLoading(false); }
  }, [authHeaders, includeCancelled]);

  useEffect(() => { load(); }, [load]);

  const filtered = rows.filter(m => {
    if (!search.trim()) return true;
    const s = search.toLowerCase();
    return (m.email || "").toLowerCase().includes(s)
      || (m.first_name || "").toLowerCase().includes(s)
      || (m.last_name || "").toLowerCase().includes(s)
      || (m.receipt_number || "").toLowerCase().includes(s);
  });

  const pendingCount = rows.filter(m => m.pending_email).length;

  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.first_name || m.email} from the members list?\n\nThis unwinds the local record — cancel via Stripe separately if they are still on a live plan.`)) return;
    try {
      await axios.delete(`${API}/admin/members/${encodeURIComponent(m.email)}`, { headers: authHeaders });
      toast.success("Member removed.");
      load();
    } catch (e) {
      toast.error(`Delete failed: ${e?.response?.data?.detail || e.message}`);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-members">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-[#0D2F4D] flex items-center gap-2">
            <Users className="h-6 w-6"/>Members
          </h2>
          <p className="text-sm text-[#6F8FA8] mt-1 max-w-2xl">
            Everyone on an active Membership subscription. Auto-populated when someone pays via the site
            <em> and </em> when Stripe webhooks catch payment-link purchases. Manually add historic buyers below.
          </p>
        </div>
        <Button
          onClick={() => setAdding(true)}
          data-testid="members-add"
          className="bg-[#0D2F4D] hover:bg-[#0D2F4D]/90 text-[#C6D21F]"
        >
          <Plus className="h-4 w-4 mr-1"/>Add member
        </Button>
      </div>

      {pendingCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl px-5 py-4 flex items-start gap-3" data-testid="members-pending-banner">
          <AlertTriangle className="h-5 w-5 text-amber-700 mt-0.5 flex-shrink-0"/>
          <div>
            <p className="font-semibold text-amber-900">{pendingCount} member{pendingCount === 1 ? "" : "s"} with a pending email</p>
            <p className="text-sm text-amber-800">These were backfilled without a real email — open each row and drop their real email address in so we can send activation + guide links.</p>
          </div>
        </div>
      )}

      {/* Quick stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card><CardContent className="pt-5 pb-4">
          <div className="text-xs uppercase tracking-wide text-[#6F8FA8] font-bold">Total members</div>
          <div className="text-3xl font-bold text-[#0D2F4D] mt-1" data-testid="members-count-total">{rows.length}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-5 pb-4">
          <div className="text-xs uppercase tracking-wide text-[#6F8FA8] font-bold">Annual</div>
          <div className="text-3xl font-bold text-emerald-700 mt-1">{rows.filter(r => r.plan === "annual").length}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-5 pb-4">
          <div className="text-xs uppercase tracking-wide text-[#6F8FA8] font-bold">Monthly</div>
          <div className="text-3xl font-bold text-blue-700 mt-1">{rows.filter(r => r.plan === "monthly").length}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-5 pb-4">
          <div className="text-xs uppercase tracking-wide text-[#6F8FA8] font-bold">Pending email</div>
          <div className="text-3xl font-bold text-amber-700 mt-1">{pendingCount}</div>
        </CardContent></Card>
      </div>

      <div className="flex gap-3 items-center">
        <div className="relative flex-1 max-w-md">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#6F8FA8]"/>
          <Input
            placeholder="Search by name, email, or receipt number…"
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="members-search"
          />
        </div>
        <label className="text-sm flex items-center gap-2 text-[#0D2F4D]">
          <input type="checkbox" checked={includeCancelled} onChange={(e) => setIncludeCancelled(e.target.checked)} data-testid="members-include-cancelled"/>
          Include cancelled
        </label>
      </div>

      <Card>
        <Table data-testid="members-table">
          <TableHeader>
            <TableRow>
              <TableHead>Member</TableHead>
              <TableHead>Plan</TableHead>
              <TableHead>Receipt</TableHead>
              <TableHead>Started</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right w-[160px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={7} className="text-center py-8 text-[#6F8FA8]">Loading…</TableCell></TableRow>}
            {!loading && filtered.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-[#6F8FA8]">
                {rows.length === 0 ? "No members yet. Click Add member to backfill historic buyers." : "No members match this search."}
              </TableCell></TableRow>
            )}
            {filtered.map((m) => (
              <TableRow key={m.email} data-testid={`member-row-${m.email}`} className={m.pending_email ? "bg-amber-50/40" : ""}>
                <TableCell>
                  <div className="font-semibold text-[#0D2F4D] flex items-center gap-2">
                    {m.first_name} {m.last_name}
                    {m.pending_email && <Badge className="bg-amber-500 text-white text-[10px]" data-testid={`member-pending-${m.email}`}>Pending email</Badge>}
                  </div>
                  <div className="text-xs text-[#6F8FA8]">{m.email}</div>
                </TableCell>
                <TableCell>
                  <Badge variant={m.plan === "annual" ? "default" : "outline"} className={m.plan === "annual" ? "bg-emerald-600" : ""}>
                    {m.plan || "—"}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs font-mono text-[#0D2F4D]">{m.receipt_number || "—"}</TableCell>
                <TableCell className="text-xs text-[#6F8FA8]">{m.started_at ? new Date(m.started_at).toLocaleDateString("en-AU") : "—"}</TableCell>
                <TableCell><Badge variant="outline" className="text-[10px]">{m.source || "stripe"}</Badge></TableCell>
                <TableCell>
                  <Badge variant={m.status === "active" ? "default" : "secondary"} className={m.status === "active" ? "bg-emerald-600" : ""}>
                    {m.status || "unknown"}
                  </Badge>
                </TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="outline" onClick={() => setEditing(m)} data-testid={`member-edit-${m.email}`}>Edit</Button>
                  <Button size="sm" variant="ghost" onClick={() => remove(m)} data-testid={`member-delete-${m.email}`} className="text-red-700 ml-1">Remove</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {adding && <MemberDialog authHeaders={authHeaders} onClose={() => setAdding(false)} onSaved={() => { setAdding(false); load(); }}/>}
      {editing && <MemberDialog authHeaders={authHeaders} member={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }}/>}
    </div>
  );
}

function MemberDialog({ authHeaders, member = null, onClose, onSaved }) {
  const isEdit = !!member;
  const [form, setForm] = useState({
    email: member?.email || "",
    first_name: member?.first_name || "",
    last_name: member?.last_name || "",
    plan: member?.plan || "monthly",
    receipt_number: member?.receipt_number || "",
    started_at: (member?.started_at || "").slice(0, 10),
    notes: member?.notes || "",
    pending_email: member?.pending_email || false,
  });
  const [saving, setSaving] = useState(false);
  const set = (patch) => setForm({ ...form, ...patch });

  const save = async () => {
    if (!form.email || form.email.indexOf("@") < 1) {
      return toast.error("Valid email required. For placeholder, use like 'ang.pending@theditchcollective.local'.");
    }
    setSaving(true);
    try {
      const startedISO = form.started_at ? new Date(form.started_at).toISOString() : new Date().toISOString();
      const payload = {
        email: form.email.trim().toLowerCase(),
        first_name: form.first_name.trim(),
        last_name: form.last_name.trim(),
        plan: form.plan,
        receipt_number: form.receipt_number.trim() || null,
        started_at: startedISO,
        notes: form.notes.trim(),
        pending_email: !!form.pending_email,
      };
      if (isEdit) {
        await axios.patch(`${API}/admin/members/${encodeURIComponent(member.email)}`, payload, { headers: authHeaders });
        toast.success("Member updated.");
      } else {
        await axios.post(`${API}/admin/members`, payload, { headers: authHeaders });
        toast.success("Member added.");
      }
      onSaved?.();
    } catch (e) {
      toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={true} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="member-dialog">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit — ${member.first_name || member.email}` : "Add member manually"}</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-[#6F8FA8]">First name</label>
              <Input value={form.first_name} onChange={(e) => set({ first_name: e.target.value })} data-testid="member-first-name"/>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-[#6F8FA8]">Last name</label>
              <Input value={form.last_name} onChange={(e) => set({ last_name: e.target.value })} data-testid="member-last-name"/>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-[#6F8FA8]">Email</label>
            <Input value={form.email} onChange={(e) => set({ email: e.target.value })} data-testid="member-email" placeholder="member@example.com"/>
            <label className="text-xs text-[#6F8FA8] mt-1 flex items-center gap-2">
              <input type="checkbox" checked={form.pending_email} onChange={(e) => set({ pending_email: e.target.checked })} data-testid="member-pending-email"/>
              Placeholder / pending — we don't have their real email yet
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-[#6F8FA8]">Plan</label>
              <Select value={form.plan} onValueChange={(v) => set({ plan: v })}>
                <SelectTrigger data-testid="member-plan"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly — $4.99</SelectItem>
                  <SelectItem value="annual">Annual — $49</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest font-bold text-[#6F8FA8]">Started on</label>
              <Input type="date" value={form.started_at} onChange={(e) => set({ started_at: e.target.value })} data-testid="member-started"/>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-[#6F8FA8]">Receipt number (optional)</label>
            <Input value={form.receipt_number} onChange={(e) => set({ receipt_number: e.target.value })} data-testid="member-receipt" placeholder="TDC-2026-0023"/>
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest font-bold text-[#6F8FA8]">Notes</label>
            <Input value={form.notes} onChange={(e) => set({ notes: e.target.value })} data-testid="member-notes" placeholder="Optional — backfill reason, source, etc."/>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="member-cancel"><X className="h-4 w-4 mr-1"/>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#0D2F4D] hover:bg-[#0D2F4D]/90 text-[#C6D21F]" data-testid="member-save">
            {saving ? "Saving…" : (isEdit ? "Save changes" : "Add member")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
